import { PagedList } from 'contensis-core-api';
import { Role } from 'contensis-management-api';
import ContensisRepository from '~/repositories/ContensisRepository';
import { Logger } from '~/util/logger';
import { combineMerge, merge } from '~/util/merge';

class RolesService {
  private contensis: ContensisRepository;

  roles: Role[] = [];
  error: AppError | null = null;

  constructor(contensis: ContensisRepository) {
    this.contensis = contensis;
  }

  GetRoles = async (): Promise<
    [ApiErrorResponse | null, Role[] | undefined]
  > => {
    Logger.info(`Getting roles from '${this.contensis.alias}'`);

    let roles: PagedList<Role> | undefined;
    [this.error, roles] = await this.contensis.repo.GetRoles();
    if (roles?.items) this.roles = roles.items;

    return [this.error, this.roles];
  };

  CreateRole = async (
    role: Omit<Role, 'id'>
  ): Promise<[ApiErrorResponse | null, Role | undefined]> => {
    if (!this.roles.length) await this.GetRoles();

    const existingRole = this.roles.find(
      (p) => p.name.toLowerCase() === role.name.toLowerCase()
    );
    if (existingRole)
      return [
        new Error(
          `A role with name "${role.name}" already exists in ${this.contensis.projectId} on ${this.contensis.alias}`
        ),
        ,
      ];

    Logger.info(`Creating role '${role.name}'`);
    const [error, created] = await this.contensis.repo.CreateRole(role as Role);

    if (error) this.error = error;

    if (created) this.roles.push(created);
    return [error, created];
  };

  UpdateRole = async (
    id: string,
    role: Partial<Role>
  ): Promise<[ApiErrorResponse | null, Role | undefined]> => {
    if (!this.roles.length) await this.GetRoles();

    const existingRole = this.roles.find(
      (p) => p.id.toLowerCase() === id.toLowerCase()
    );
    if (!existingRole)
      return [
        new Error(
          `A role with id ${id} was not found in ${this.contensis.alias} environment`
        ),
        ,
      ];

    const updateRole = merge(existingRole, role, {
      arrayMerge: combineMerge,
    });
    // Look for permissions arrays that contain an ALL wildcard, but have
    // other granular permissions and vice versa
    for (const p of role.permissions?.entries || []) {
      // Locate the changed permission in the merged role data to update
      const u = updateRole.permissions.entries?.find((i) => i.id === p.id);

      if (u && p.actions?.find((a) => typeof a === 'string' && a === '*'))
        // Replace all the actions in the update permission with the inbound
        // permissions containing a wildcard
        u.actions = p.actions;

      if (
        u &&
        u.actions?.length &&
        p.actions?.length &&
        !p.actions?.find((a) => typeof a === 'string' && a === '*')
      )
        // Ensure the update permission has any existing wildcard removed if the inbound
        // permission does not contain a wildcard
        u.actions = u.actions.filter((a) => a !== '*');

      if (u && p.languages?.find((a) => typeof a === 'string' && a === '*'))
        // Replace all the languages in the update permission with the inbound
        // permissions containing a wildcard
        u.languages = p.languages;

      if (
        u &&
        u.languages?.length &&
        p.languages?.length &&
        !p.languages?.find((a) => typeof a === 'string' && a === '*')
      )
        // Ensure the update permission has any existing wildcard removed if the inbound
        // permission does not contain a wildcard
        u.languages = u.languages.filter((a) => a !== '*');

      // Add in wildcards for permission actions and languages if there are none
      // found in the merged update permission
      if (u?.id && !u.actions?.length) u.actions = ['*'];
      if (u?.id && !u.languages?.length) u.languages = ['*'];
    }

    Logger.info(`Updating role '${role.name}'`);

    if (!this.error && this.roles.length) {
      const [error, updated] = await this.contensis.repo.UpdateRole(updateRole);
      if (error) this.error = error;

      if (updated) {
        this.roles = this.roles.filter((k) => k.id !== id);
        this.roles.push(updated);
      }
      return [error, updated];
    } else {
      return [this.error, undefined];
    }
  };

  RemoveRole = async (
    id: string
  ): Promise<[ApiErrorResponse | null, undefined]> => {
    if (!this.roles.length) await this.GetRoles();

    const existingRole = this.roles.find(
      (p) => p.id.toLowerCase() === id.toLowerCase()
    );
    if (!existingRole)
      return [
        new Error(
          `A role with id ${id} was not found in ${this.contensis.alias} environment`
        ),
        ,
      ];

    Logger.info(`Removing role '${id}'`);
    [this.error] = await this.contensis.repo.DeleteRole(id);
    return [this.error, undefined];
  };
}

export default RolesService;
